import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

export interface WeeklyHoursReportSummary {
  id: number;
  etablissementId: number;
  etablissementName: string;
  agencyId?: number;
  weekNumber: number;
  /** WEEK (lundi → dimanche) ou MONTH (mois civil complet, agence en mode « mensuel »). */
  periodType?: 'WEEK' | 'MONTH';
  /** « Semaine 40 » ou « Septembre 2026 ». */
  periodLabel?: string;
  year: number;
  weekStart: string;
  weekEnd: string;
  /** Correction client (P5) : un relevé par fonction — 1 = Médecins, 2 = Infirmiers (null = ancien relevé combiné). */
  typeSecteurId?: number | null;
  fonctionLabel?: string;
  status: 'DRAFT' | 'READY' | 'SENT';
  sentAt?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  allowedSendFrom?: string;
  canSendNow?: boolean;
}

export interface WeeklyHoursReportLine {
  id: number;
  missionId: number;
  missionRef?: string;
  candidatId: number;
  candidatFullname: string;
  weekNumber?: number;
  startDate: string;
  endDate: string;
  startTime: string | null;
  endTime: string | null;
  pauseMinutes?: number | null;
  totalHours?: number | null;
  lineState: 'EN_COURS' | 'ACHEVE' | 'ANNULE';
  observations?: string;
  edited?: boolean;
  typeSecteurId?: number | null;
  fonctionLabel?: string | null;
}

export interface WeeklyHoursReportDetail extends WeeklyHoursReportSummary {
  lines: WeeklyHoursReportLine[];
}

// E1 (client clinique — P5) : config relevé par agence
export interface WeeklyHoursConfig {
  agencyId: number;
  frequency: 'WEEKLY' | 'MONTHLY';
  accountantEmail?: string | null;
}

// E4 : soumission de relevé d'heures en attente de validation agence
export interface HoursSubmissionSummary {
  missionId: number;
  missionRef?: string;
  agencyId?: number;
  startDate?: string;
  endDate?: string;
  candidatId?: number;
  candidatFullname?: string;
  submittedAt?: string;
  status: string;
  dayCount?: number;
}

export interface HoursSubmissionRow {
  id: number;
  workingDate: string;
  actualStartTime: string | null;
  actualEndTime: string | null;
  actualPauseMinutes?: number | null;
  comment?: string;
  status: string;
  originalStartTime?: string | null;
  originalEndTime?: string | null;
  originalPauseMinutes?: number | null;
}

export interface HoursSubmissionDetail extends HoursSubmissionSummary {
  rows: HoursSubmissionRow[];
}

@Injectable({ providedIn: 'root' })
export class WeeklyHoursService {
  private base = `${environment.api.BASE_URL}/api/agency/weekly-hours`;

  constructor(private http: HttpClient) {}

  list(): Observable<WeeklyHoursReportSummary[]> {
    return this.http.get<WeeklyHoursReportSummary[]>(this.base);
  }

  get(id: number): Observable<WeeklyHoursReportDetail> {
    return this.http.get<WeeklyHoursReportDetail>(`${this.base}/${id}`);
  }

  updateLine(reportId: number, lineId: number, edits: Partial<WeeklyHoursReportLine>): Observable<WeeklyHoursReportLine> {
    return this.http.put<WeeklyHoursReportLine>(`${this.base}/${reportId}/line/${lineId}`, edits);
  }

  regenerate(reportId: number): Observable<WeeklyHoursReportDetail> {
    return this.http.post<WeeklyHoursReportDetail>(`${this.base}/${reportId}/regenerate`, {});
  }

  /** Correction client (P5) : le PDF est celui de la fonction du relevé ;
   *  ?fonction=medecins|infirmiers ne sert qu'aux anciens relevés combinés. */
  pdf(reportId: number, fonction?: 'medecins' | 'infirmiers'): Observable<{ contractBytes: string }> {
    const q = fonction ? `?fonction=${fonction}` : '';
    return this.http.get<{ contractBytes: string }>(`${this.base}/${reportId}/pdf${q}`);
  }

  send(reportId: number): Observable<{ status: string; sentAt: string }> {
    // Fix : le back attend POST /{id}/send (l'id manquait → 404 sur « Valider et envoyer »).
    return this.http.post<{ status: string; sentAt: string }>(`${this.base}/${reportId}/send`, {});
  }

  // ────────────────────────────────────────────────────────────
  // E1 (client clinique — P5) : configuration (périodicité + e-mail comptable)
  // ────────────────────────────────────────────────────────────

  getConfig(agencyId: number): Observable<WeeklyHoursConfig> {
    return this.http.get<WeeklyHoursConfig>(`${this.base}/config?agencyId=${agencyId}`);
  }

  updateConfig(config: WeeklyHoursConfig): Observable<WeeklyHoursConfig> {
    return this.http.put<WeeklyHoursConfig>(`${this.base}/config`, config);
  }

  // ────────────────────────────────────────────────────────────
  // E4 : validation des relevés d'heures envoyés par les pros
  // ────────────────────────────────────────────────────────────

  listSubmissions(agencyId?: number): Observable<HoursSubmissionSummary[]> {
    const url = agencyId ? `${environment.api.BASE_URL}/api/agency/hours-validations?agencyId=${agencyId}`
      : `${environment.api.BASE_URL}/api/agency/hours-validations`;
    return this.http.get<HoursSubmissionSummary[]>(url);
  }

  getSubmission(missionId: number): Observable<HoursSubmissionDetail> {
    return this.http.get<HoursSubmissionDetail>(`${environment.api.BASE_URL}/api/agency/hours-validations/${missionId}`);
  }

  editSubmissionRow(missionId: number, rowId: number, edits: { actualStartTime?: string; actualEndTime?: string; actualPauseMinutes?: number | null; comment?: string }): Observable<HoursSubmissionRow> {
    return this.http.put<HoursSubmissionRow>(`${environment.api.BASE_URL}/api/agency/hours-validations/${missionId}/row/${rowId}`, edits);
  }

  validateSubmission(missionId: number): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${environment.api.BASE_URL}/api/agency/hours-validations/${missionId}/validate`, {});
  }

  rejectSubmission(missionId: number, motif: string): Observable<{ status: string }> {
    return this.http.post<{ status: string }>(`${environment.api.BASE_URL}/api/agency/hours-validations/${missionId}/reject`, { motif });
  }
}
